import { buildEndpointUrls } from "./apiUrls";

export function normalizeTeamMemberName(value) {
  return String(value || "").replace(/\s+/g, " ").trim();
}

export function upsertTeamMemberOption(list, teamMember) {
  const normalizedName = normalizeTeamMemberName(teamMember?.name);
  if (!normalizedName) return Array.isArray(list) ? list : [];

  const normalizedId = teamMember?.id != null ? String(teamMember.id) : null;
  const nextOption = {
    id: teamMember?.id ?? null,
    name: normalizedName,
  };

  const existing = Array.isArray(list) ? list : [];
  const deduped = existing.filter((item) => {
    if (normalizedId && item?.id != null && String(item.id) === normalizedId) return false;
    return normalizeTeamMemberName(item?.name).toLowerCase() !== normalizedName.toLowerCase();
  });

  return [...deduped, nextOption].sort((a, b) => a.name.localeCompare(b.name));
}

function getToken(explicitToken) {
  return (
    explicitToken ||
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("jwt") ||
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("token") ||
    sessionStorage.getItem("authToken") ||
    sessionStorage.getItem("jwt") ||
    sessionStorage.getItem("accessToken") ||
    ""
  );
}

function normalizeTeamMemberRecord(value) {
  const name = normalizeTeamMemberName(
    value?.name || value?.team_member || value?.teamMember || value?.assigned_team_member || ""
  );
  if (!name) return null;

  return {
    id: value?.id ?? null,
    name,
  };
}

function normalizeRecruiterUserRecord(value) {
  const name = normalizeTeamMemberName(value?.name || value?.full_name || "");
  if (!name) return null;

  return {
    id: value?.id ?? value?.user_id ?? null,
    name,
    email: String(value?.email || "").trim(),
    role: String(value?.role || "").trim(),
    avatar_url: String(
      value?.avatar_url || value?.profile_image || value?.avatar || value?.photo_url || ""
    ).trim(),
  };
}

async function requestJson({ url, method = "GET", token, body }) {
  const headers = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
    headers["x-access-token"] = token;
  }
  if (body !== undefined) headers["Content-Type"] = "application/json";

  const res = await fetch(url, {
    method,
    headers,
    credentials: "include",
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  const data = await res.json().catch(() => ({}));
  return { res, data };
}

function extractTeamMembersList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.teamMembers)) return payload.teamMembers;
  if (Array.isArray(payload?.data?.rows)) return payload.data.rows;
  if (Array.isArray(payload?.data?.teamMembers)) return payload.data.teamMembers;
  return [];
}

function extractSingleTeamMember(payload) {
  return normalizeTeamMemberRecord(
    payload?.teamMember ||
      payload?.team_member ||
      payload?.data?.teamMember ||
      payload?.data?.team_member ||
      payload?.data ||
      payload
  );
}

function extractRecruiterUsers(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.users)) return payload.users;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.data?.users)) return payload.data.users;
  if (Array.isArray(payload?.data?.rows)) return payload.data.rows;
  return [];
}

export async function fetchTeamMembers({ apiBase, token }) {
  const authToken = getToken(token);
  const urls = buildEndpointUrls(
    apiBase,
    "/api/team-members?limit=500",
    "/team-members?limit=500"
  );

  let lastError = null;

  for (const url of urls) {
    try {
      const { res, data } = await requestJson({
        url,
        method: "GET",
        token: authToken,
      });

      if (!res.ok) {
        const nextError = new Error(
          data?.error || data?.message || `Failed to load team members (${res.status})`
        );
        nextError.status = res.status;
        if (res.status !== 404) throw nextError;
        lastError = nextError;
        continue;
      }

      return extractTeamMembersList(data)
        .map(normalizeTeamMemberRecord)
        .filter(Boolean)
        .sort((a, b) => a.name.localeCompare(b.name));
    } catch (error) {
      lastError = error;
    }
  }

  if (lastError) throw lastError;
  return [];
}

export async function fetchTeamRoster({ apiBase, token }) {
  const authToken = getToken(token);
  const urls = buildEndpointUrls(
    apiBase,
    "/api/team-members/roster",
    "/team-members/roster"
  );

  let lastError = null;
  for (const url of urls) {
    try {
      const { res, data } = await requestJson({ url, method: "GET", token: authToken });
      if (!res.ok) {
        const nextError = new Error(
          data?.error || data?.message || `Failed to load team roster (${res.status})`
        );
        nextError.status = res.status;
        if (res.status !== 404) throw nextError;
        lastError = nextError;
        continue;
      }

      return extractTeamMembersList(data)
        .map((value) => {
          const name = normalizeTeamMemberName(value?.name || value?.team_member);
          if (!name) return null;
          return {
            id: value?.user_id ?? value?.team_member_id ?? value?.id ?? null,
            name,
            email: String(value?.email || "").trim(),
            role: String(value?.role || "team member").trim(),
            title: String(value?.title || "").trim(),
            avatar_url: String(value?.avatar_url || value?.profile_image || "").trim(),
            status: String(value?.status || value?.availability || "").trim(),
            location: String(value?.location || "").trim(),
            company: String(value?.company || "").trim(),
            assignments_count: Number(value?.assignments_count || 0),
            candidates_count: Number(value?.candidates_count || 0),
            clients_count: Number(value?.clients_count || 0),
            activities_count: Number(value?.activities_count || 0),
            last_activity: value?.last_activity || null,
            last_activity_description: String(value?.last_activity_description || "").trim(),
            recruiting_metrics: value?.recruiting_metrics || {},
            sales_metrics: value?.sales_metrics || {},
          };
        })
        .filter(Boolean)
        .sort((a, b) => a.name.localeCompare(b.name));
    } catch (error) {
      lastError = error;
    }
  }

  if (lastError) throw lastError;
  return [];
}

export async function fetchRecruiterUsers({ apiBase, token }) {
  const authToken = getToken(token);
  const urls = buildEndpointUrls(
    apiBase,
    "/api/users/recruiters?limit=300",
    "/users/recruiters?limit=300"
  );

  let lastError = null;

  for (const url of urls) {
    try {
      const { res, data } = await requestJson({
        url,
        method: "GET",
        token: authToken,
      });

      if (!res.ok) {
        const nextError = new Error(
          data?.error || data?.message || `Failed to load recruiter users (${res.status})`
        );
        nextError.status = res.status;
        if (res.status !== 404) throw nextError;
        lastError = nextError;
        continue;
      }

      return extractRecruiterUsers(data)
        .map(normalizeRecruiterUserRecord)
        .filter(Boolean)
        .sort((a, b) => a.name.localeCompare(b.name));
    } catch (error) {
      lastError = error;
    }
  }

  if (lastError) throw lastError;
  return [];
}

export async function createTeamMemberIfMissing({
  apiBase,
  token,
  name,
  existing = [],
}) {
  const normalizedName = normalizeTeamMemberName(name);
  if (!normalizedName) return null;

  const matchingExisting = (Array.isArray(existing) ? existing : []).find(
    (item) => normalizeTeamMemberName(item?.name).toLowerCase() === normalizedName.toLowerCase()
  );
  if (matchingExisting) {
    return normalizeTeamMemberRecord(matchingExisting);
  }

  const authToken = getToken(token);
  const urls = buildEndpointUrls(apiBase, "/api/team-members", "/team-members");
  const payloads = [
    { name: normalizedName },
    { team_member: normalizedName },
    { teamMember: normalizedName },
    { assigned_team_member: normalizedName },
  ];

  let lastError = null;

  for (const url of urls) {
    for (const body of payloads) {
      try {
        const { res, data } = await requestJson({
          url,
          method: "POST",
          token: authToken,
          body,
        });

        if (!res.ok) {
          lastError = new Error(
            data?.error || data?.message || `Failed to create team member (${res.status})`
          );
          continue;
        }

        const created = extractSingleTeamMember(data);
        if (created) return created;
      } catch (error) {
        lastError = error;
      }
    }
  }

  const refreshed = await fetchTeamMembers({ apiBase, token: authToken }).catch(() => []);
  const foundAfterRefresh = refreshed.find(
    (item) => item.name.toLowerCase() === normalizedName.toLowerCase()
  );
  if (foundAfterRefresh) return foundAfterRefresh;

  throw lastError || new Error("Failed to create team member");
}
